import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useMediaQuery } from '@wkda/styleguide';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useTrackEvent, useCarleadHashParam, useAppConfiguration } from '@wkda/funnel-components';
import CloseButtonIconGray from './images/x-gray.svg';
import CloseButtonIconBlue from './images/x-blue.svg';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function SlideModal(props) {
  const {
    isOpen,
    id,
    close,
    closeButtonIcon,
    closeButtonVariant = 'gray',
    title,
    children,
    headerImage,
    trackingCategory = 'Slide Modal',
    trackingLabel = 'Automatic',
    ariaLabel,
    ariaDescribedBy,
    formInteracted,
    showMobileDragIndicator = false,
    onBeforeClose,
    htmlHiddenWhenNotOpen = false,
    disableTouchEvents = false
  } = props;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const modalRef = useRef(null);
  const hasMoved = useRef(false);
  const previousActiveElement = useRef(null);
  const isDesktop = useMediaQuery('(min-width: 576px)');
  const isMobile = !isDesktop;
  const touchEventsEnabled = isMobile && !disableTouchEvents;
  const defaultCloseIcon = closeButtonVariant === 'blue' ? CloseButtonIconBlue : CloseButtonIconGray;
  const yDownRef = useRef(null);
  const track = useTrackEvent();
  const hash = useCarleadHashParam();
  const {
    trackLandingPageType
  } = useAppConfiguration();
  const modalId = id || 'slide-modal';
  const titleId = `${modalId}-title`;
  const contentId = `${modalId}-content`;
  const classnames = cx('slide-modal', {
    'slide-modal__open': isOpen,
    [`slide-modal__${id}`]: id,
    'slide-modal--mounted': isMounted
  });

  // add mounted class after initial render to avoid unwanted transitions
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const handleClose = useCallback(method => e => {
    // callback function that can be used to trigger an event when the modal is closed
    if (onBeforeClose) {
      onBeforeClose();
    }
    if (close) {
      close(e);
      document.body.classList.remove('slide-modal__scrolling-disabled');
      track({
        action: 'eventTracking',
        category: trackLandingPageType,
        eventData: {
          category: trackingCategory,
          action: 'Close',
          label: method,
          ...(hash && {
            hash
          }),
          ...(formInteracted !== undefined && {
            formInteraction: formInteracted
          })
        }
      });
    }
  }, [close, track, trackingCategory, hash, trackLandingPageType, formInteracted, onBeforeClose]);

  // Focus management and escape key handling
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Focus the modal
      const focusableElements = modalRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusableElements?.length) {
        focusableElements[0].focus();
      } else {
        modalRef.current?.focus();
      }

      // Handle escape key
      const handleEscape = e => {
        if (e.key === 'Escape') {
          handleClose('Escape Key')(e);
        }
      };

      // Trap focus within modal
      const handleTabKey = e => {
        if (e.key === 'Tab') {
          const focusableElements = modalRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
          if (focusableElements?.length) {
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (e.shiftKey && document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      };
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTabKey);
      document.body.classList.add('slide-modal__scrolling-disabled');
      track({
        action: 'eventTracking',
        category: trackLandingPageType,
        eventData: {
          category: trackingCategory,
          action: 'Open',
          label: trackingLabel,
          ...(hash && {
            hash
          }),
          ...(formInteracted !== undefined && {
            formInteraction: formInteracted
          })
        }
      });
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', handleTabKey);
      };
    } else {
      // Restore focus to previously focused element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
        previousActiveElement.current = null;
      }

      // we have to wait for css animation to finish before we allow scrolling
      const animationDelay = 300;
      const timer = setTimeout(() => {
        document.body.classList.remove('slide-modal__scrolling-disabled');
      }, animationDelay);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, track, trackingCategory, trackingLabel, hash, trackLandingPageType, formInteracted]);

  // Define touch handlers with useCallback to maintain reference equality
  const handleTouchStart = useCallback(evt => {
    if (!isOpen || !touchEventsEnabled) return;
    const firstTouch = evt.touches[0];
    yDownRef.current = firstTouch.clientY;
    // Don't set isSwiping right away, wait for movement
    hasMoved.current = false;
  }, [isOpen, touchEventsEnabled]);
  const handleTouchMove = useCallback(evt => {
    if (!yDownRef.current || !isOpen || !touchEventsEnabled) return;
    const yUp = evt.touches[0].clientY;
    const yDiff = yUp - yDownRef.current;

    // Only consider it a swipe if moved significantly down
    if (yDiff > 5) {
      if (!hasMoved.current) {
        hasMoved.current = true;
        setIsSwiping(true);
      }

      // Only allow downward swiping
      setSwipeOffset(Math.min(yDiff, 300)); // Limit max swipe distance
      evt.preventDefault();
    }
  }, [isOpen, touchEventsEnabled]);
  const handleTouchEnd = useCallback(() => {
    if (!isOpen || !touchEventsEnabled) return;
    if (hasMoved.current && swipeOffset > 100) {
      // Close modal when swiped down past threshold
      handleClose('Modal Swipe Down')();
      document.body.classList.remove('slide-modal__scrolling-disabled');
    } else {
      setSwipeOffset(0); // Reset position if not swiped far enough
    }
    setIsSwiping(false);
    yDownRef.current = null;
    hasMoved.current = false;
  }, [swipeOffset, isOpen, touchEventsEnabled, handleClose]);

  // Setup and cleanup touch event listeners
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, {
      passive: false
    });
    document.addEventListener('touchmove', handleTouchMove, {
      passive: false
    });
    document.addEventListener('touchend', handleTouchEnd, {
      passive: false
    });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: classnames,
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": title ? titleId : undefined,
      "aria-label": !title ? ariaLabel || 'Modal dialog' : undefined,
      "aria-describedby": ariaDescribedBy || contentId,
      hidden: htmlHiddenWhenNotOpen && !isOpen,
      children: /*#__PURE__*/_jsxs("div", {
        className: "slide-modal__slider",
        ref: modalRef,
        tabIndex: -1,
        style: {
          transform: isSwiping ? `translate(0, ${swipeOffset}px)` : null
        },
        children: [showMobileDragIndicator && /*#__PURE__*/_jsx("div", {
          className: "slide-modal__drag-indicator",
          "aria-hidden": "true"
        }), closeButtonVariant !== 'none' && /*#__PURE__*/_jsx("button", {
          className: "slide-modal__close-button",
          onClick: handleClose('Cross'),
          type: "button",
          "aria-label": "Close modal",
          children: /*#__PURE__*/_jsx("img", {
            className: "slide-modal__close-button-icon",
            src: closeButtonIcon || defaultCloseIcon,
            alt: "",
            role: "presentation"
          })
        }), headerImage && /*#__PURE__*/_jsx(HeaderImage, {
          image: headerImage
        }), /*#__PURE__*/_jsxs("div", {
          className: "slide-modal__content",
          id: contentId,
          children: [title && /*#__PURE__*/_jsx("h3", {
            className: "slide-modal__title",
            id: titleId,
            children: title
          }), children]
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "slide-modal__overlay",
      onClick: handleClose('Overlay'),
      "aria-hidden": "true",
      hidden: htmlHiddenWhenNotOpen && !isOpen
    })]
  });
}
const HeaderImage = _ref => {
  let {
    image
  } = _ref;
  if (!image) return null;
  const {
    mobileSrc,
    desktopSrc,
    src,
    alt
  } = image;
  if (mobileSrc && desktopSrc) {
    return /*#__PURE__*/_jsxs("picture", {
      children: [/*#__PURE__*/_jsx("source", {
        media: "(min-width: 768px)",
        srcSet: desktopSrc
      }), /*#__PURE__*/_jsx("img", {
        src: mobileSrc,
        alt: alt || '',
        className: "slide-modal__header-image"
      })]
    });
  }
  if (src) {
    return /*#__PURE__*/_jsx("img", {
      src: src,
      alt: alt || '',
      className: "slide-modal__header-image"
    });
  }
  return null;
};
SlideModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  closeButtonIcon: PropTypes.string,
  closeButtonVariant: PropTypes.oneOf(['gray', 'blue']),
  children: PropTypes.node.isRequired,
  headerImage: PropTypes.shape({
    mobileSrc: PropTypes.string,
    desktopSrc: PropTypes.string,
    src: PropTypes.string,
    alt: PropTypes.string,
    srcSet: PropTypes.string,
    sizes: PropTypes.string
  }),
  id: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  trackingCategory: PropTypes.string,
  trackingLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  formInteracted: PropTypes.bool,
  showMobileDragIndicator: PropTypes.bool,
  onBeforeClose: PropTypes.func,
  htmlHiddenWhenNotOpen: PropTypes.bool,
  disableTouchEvents: PropTypes.bool
};